/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Box,
  FormControl,
  MenuItem,
  SelectChangeEvent,
  TextField,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton, RobotoTypography } from "@/shared/components";
import { Token } from "@/shared/domain/tokens";

import { CreateTokenSuccessModal } from "./CreateTokenSuccessModal";

const EXPIRY_OPTIONS = [
  {
    label: "1 Year",
    value: 365,
  },
  {
    label: "3 Months",
    value: 90,
  },
  {
    label: "1 Month",
    value: 30,
  },
  {
    label: "1 Week",
    value: 7,
  },
  {
    label: "1 Day",
    value: 1,
  },
];

interface CreateTokensFormProps {
  refetch: () => void;
  setErrorText: (arg: string) => void;
}

export const CreateTokenForm: React.FC<CreateTokensFormProps> = ({
  refetch,
  setErrorText,
}) => {
  const theme = useTheme();
  const [expiry, setExpiry] = useState(EXPIRY_OPTIONS[0].value.toString());
  const [tokenName, setTokenName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [createdToken, setCreatedToken] = useState<Token>();

  const menuItems = EXPIRY_OPTIONS.map((value, idx) => {
    return (
      <MenuItem key={`create-token-expiry-${idx}`} value={value.value}>
        {value.label}
      </MenuItem>
    );
  });

  const { tokens } = useDomainServices();

  const createToken = () => {
    // Clear any prior state
    setErrorText("");
    setCreatedToken(undefined);

    tokens
      .createToken(Number.parseInt(expiry), tokenName)
      .then((token: Token) => {
        setShowModal(true);
        setCreatedToken(token);
        refetch();
      })
      .catch((err: unknown) => {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to create token", {
                cause: err,
              });
        setErrorText(error.message);
      });
  };

  return (
    <Box>
      {showModal && createdToken && (
        <CreateTokenSuccessModal
          token={createdToken}
          open={showModal}
          close={() => {
            setShowModal(false);
          }}
        />
      )}

      <RobotoTypography
        variant="body1"
        sx={{
          marginBottom: theme.spacing(2),
        }}
      >
        Create a new access token
      </RobotoTypography>

      <FormControl sx={{ display: "flex", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(2),
          }}
        >
          <TextField
            label={"Name"}
            value={tokenName}
            onChange={(event) => {
              setTokenName(event.target.value);
            }}
            size="small"
            helperText="Give your token a memorable name e.g. Benji's MBP"
            sx={{ flex: 1 }}
          />

          <TextField
            id="create-token-select"
            select
            label="Expiry"
            value={expiry}
            size="small"
            SelectProps={{
              onChange: (event: SelectChangeEvent<unknown>) => {
                setExpiry(event.target.value as string);
              },
            }}
            sx={{
              minWidth: "100px",
              flex: 1,
            }}
            helperText="Time to keep the token alive"
          >
            {menuItems}
          </TextField>

          <RobotoButton
            data-cy="create-token"
            eventName={"CreateTokenClicked"}
            onClick={() => {
              void createToken();
            }}
            variant="contained"
            disabled={!tokenName}
            sx={{ height: 40, width: "100px" }}
          >
            Create
          </RobotoButton>
        </Box>
      </FormControl>
    </Box>
  );
};
